// QV-7124 — drives the mKonnekt manufacturer-discount lookup for the register cart.
//
// Web port of Android `OrderCreateViewModel.triggerDeziDiscounts`:
//   cart change → 250 ms debounce → build request → dedup by request key → fetch → store the result.
// The cart slice re-derives the actual per-line discount from the stored result, so this hook owns
// only WHEN to ask and WHAT was asked — never any money.
//
// Every failure resolves to EMPTY (see engine/dezi mapDeziDiscounts): a manufacturer-discount lookup
// must never be able to block a sale.

import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDeziDiscounts } from "../../../Redux/features/WebRegister/cartSlice";
import { nodeDeziDiscounts } from "../data/api";
import { auth } from "../data/api";
import {
  DEZI_EMPTY,
  buildDeziDiscountRequest,
  deziRequestKey,
  deziUpcOf,
  deziUpcSetOf,
  foldSeenDeziUpcs,
  mapDeziDiscounts,
  withDeziQuote,
} from "../engine/dezi";
import { createDeziLookupScheduler } from "./deziLookupScheduler";

const DEBOUNCE_MS = 250;
/** Upper bound on how long checkout will wait for an in-flight lookup before charging anyway. */
export const DEZI_WAIT_TIMEOUT_MS = 5000;

const SEEN_KEY = (mid) => `qv_web_register_dezi_seen_${mid || ""}`;

/**
 * `seenDeziUpcs` — upcs mKonnekt has EVER discounted on this device, persisted per merchant.
 *
 * ⚠️ UX ONLY, NEVER MONEY. It exists solely to decide whether making the cashier wait is warranted.
 * `deziUpcSet` (derived from the live result) remains the sole discount authority.
 *
 * Why it must persist across cart clears: the alternative signals are both wrong. Gating on the live
 * result alone makes a cart of only NON-dezi products wait 1–2 s for a discount that is never coming
 * (it is empty until the first response), while not gating at all lets the very first add of a real
 * dezi product be tendered at the pre-discount total.
 *
 * ⚠️ It is a HEURISTIC and cannot be otherwise — mKonnekt discounts are conditional (the amount
 * scales with quantity and a threshold deal returns nothing for a small cart), so "is this upc dezi"
 * has no stable client-side answer.
 */
function loadSeen(merchantId) {
  try {
    const raw = localStorage.getItem(SEEN_KEY(merchantId));
    return new Set(raw ? raw.split(",").filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveSeen(merchantId, set) {
  try {
    localStorage.setItem(SEEN_KEY(merchantId), Array.from(set).join(","));
  } catch {
    /* private mode / quota — the memory is an optimisation, never correctness */
  }
}

export function clearSeenDeziUpcs(merchantId) {
  try {
    localStorage.removeItem(SEEN_KEY(merchantId));
  } catch {
    /* ignore */
  }
}

/**
 * @param enabled   the store gate (is_dezi_enabled === "1" && rcn non-blank)
 * @param rcn       merchant rcn
 * @param customerPhone attached customer's phone, or "" — LOAD-BEARING, see deziRequestKey
 * @returns { waitWarranted, awaitSettled }
 */
export function useDeziDiscounts({ enabled, rcn, customerPhone }) {
  const dispatch = useDispatch();
  const lines = useSelector((s) => s.webRegisterCart.lines);
  const deziDiscounts = useSelector((s) => s.webRegisterCart.deziDiscounts);

  const seenRef = useRef(null);
  // Debounce + dedup + settle tracking. Lives for the component's lifetime so a re-run of the effect
  // for the SAME basket (a new `lines` reference) never cancels the lookup already under way.
  const lookupRef = useRef(null);
  const merchantId = auth().merchantId;

  if (seenRef.current === null) seenRef.current = loadSeen(merchantId);
  if (lookupRef.current === null) lookupRef.current = createDeziLookupScheduler({ debounceMs: DEBOUNCE_MS });

  // Unmount only: drop whatever is pending so a late response can't land in a closed register.
  useEffect(() => () => lookupRef.current.reset(), []);

  useEffect(() => {
    const lookup = lookupRef.current;
    if (!enabled) {
      lookup.reset(); // re-enable ⇒ a fresh cart re-fetches once
      return;
    }
    const request = buildDeziDiscountRequest({
      lines,
      rcn,
      mobileNumber: customerPhone || "",
    });
    if (request.items.length === 0) {
      // No line with a UPC ⇒ nothing to look up (no discounts, not a failure). Reset so the next real
      // add fetches, and drop any stale result so a cleared cart can't keep showing a discount.
      lookup.reset();
      if (deziDiscounts) dispatch(setDeziDiscounts(null));
      return;
    }
    const key = deziRequestKey(request);
    // Unchanged basket ⇒ the pending (or settled) lookup stands; a changed one supersedes it.
    lookup.request(key, (isCancelled) => {
      const requestedUpcs = new Set(request.items.map((i) => i.upcCode).filter(Boolean));
      return nodeDeziDiscounts(request)
        .then(({ ok, body }) => {
          const result = mapDeziDiscounts(ok, body);
          if (isCancelled()) return result;
          // Only fold the memory on a CONFIRMED success. The repository maps every failure to EMPTY,
          // which is indistinguishable from "no discounts" by content — expiring on a blip would drop
          // a known dezi upc and let its next sale skip the wait and charge the pre-discount total.
          if (result.success) {
            const next = foldSeenDeziUpcs(seenRef.current, deziUpcSetOf(result), requestedUpcs);
            seenRef.current = next;
            saveSeen(merchantId, next);
          }
          // The quote (phone / rcn / raw response) rides on the result it produced, so the order saved
          // at checkout reports exactly the lookup whose discount was applied.
          dispatch(setDeziDiscounts(withDeziQuote(result, request, body)));
          return result;
        })
        .catch(() => {
          if (!isCancelled()) dispatch(setDeziDiscounts(DEZI_EMPTY));
          return DEZI_EMPTY;
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rcn, customerPhone, lines]);

  /**
   * Should the cashier be made to wait? ONLY when a lookup is pending (debounced or in flight) AND
   * the cart holds a line we have reason to believe is dezi. Gating on the raw pending flag alone
   * made EVERY sale at a dezi store show "Applying manufacturer discount…" — most sales there are
   * ordinary products.
   */
  const waitWarranted = useCallback(() => {
    if (!enabled || !lookupRef.current.hasPending()) return false;
    const live = deziUpcSetOf(deziDiscounts);
    const seen = seenRef.current || new Set();
    if (live.size === 0 && seen.size === 0) return false;
    return lines.some((l) => {
      const upc = deziUpcOf(l);
      return !!upc && (live.has(upc) || seen.has(upc));
    });
  }, [enabled, deziDiscounts, lines]);

  /**
   * Wait for the pending lookup for the CURRENT basket to settle before charging — including one still
   * inside its debounce window, or one that replaced a superseded lookup mid-wait — bounded by
   * DEZI_WAIT_TIMEOUT_MS. Resolves false on timeout (checkout then saves no quote unless the slice's
   * result matches the charged cart; see engine/dezi deziSaveOrderFields).
   *
   * WHY (money): the payment screen reads the totals live, so tendering with an unresolved
   * manufacturer discount charges and SAVES the pre-discount total. Attaching a customer on the
   * payment screen re-triggers the lookup (it can unlock a loyalty-funded discount), which is exactly
   * when this matters most.
   */
  const awaitSettled = useCallback(async () => {
    if (!enabled) return true;
    return lookupRef.current.awaitSettled(DEZI_WAIT_TIMEOUT_MS);
  }, [enabled]);

  return { waitWarranted, awaitSettled };
}
